import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { CheckCircle2, XCircle, Loader2, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";

export default function NewsletterUnsubscribe() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const [status, setStatus] = useState<"loading" | "success" | "error" | "already">("loading");
  const [message, setMessage] = useState("");
  const [email, setEmail] = useState("");

  useEffect(() => {
    const unsubscribe = async () => {
      if (!token) {
        setStatus("error");
        setMessage("Invalid unsubscribe link");
        return;
      }

      try {
        const { data, error } = await supabase.functions.invoke("unsubscribe-newsletter", {
          body: { token },
        });

        if (error) throw error;

        if (data.success) {
          setEmail(data.email);
          if (data.alreadyUnsubscribed) {
            setStatus("already");
            setMessage(data.message);
          } else {
            setStatus("success");
            setMessage(data.message);
          }
        } else {
          setStatus("error");
          setMessage(data.error || "Failed to unsubscribe");
        }
      } catch (error: any) {
        console.error("Error unsubscribing from newsletter:", error);
        setStatus("error");
        setMessage(error.message || "Failed to unsubscribe");
      }
    };

    unsubscribe();
  }, [token]);

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-background via-background to-muted/20">
      <Card className="max-w-md w-full">
        <CardHeader className="text-center space-y-4">
          {status === "loading" && (
            <div className="mx-auto w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
              <Loader2 className="w-8 h-8 text-primary animate-spin" />
            </div>
          )}
          {status === "success" && (
            <div className="mx-auto w-16 h-16 rounded-full bg-success/10 flex items-center justify-center">
              <CheckCircle2 className="w-8 h-8 text-success" />
            </div>
          )}
          {status === "already" && (
            <div className="mx-auto w-16 h-16 rounded-full bg-muted/50 flex items-center justify-center">
              <Mail className="w-8 h-8 text-muted-foreground" />
            </div>
          )}
          {status === "error" && (
            <div className="mx-auto w-16 h-16 rounded-full bg-destructive/10 flex items-center justify-center">
              <XCircle className="w-8 h-8 text-destructive" />
            </div>
          )}
          
          <CardTitle className="text-2xl">
            {status === "loading" && "Processing Unsubscribe..."}
            {status === "success" && "Successfully Unsubscribed"}
            {status === "already" && "Already Unsubscribed"}
            {status === "error" && "Unsubscribe Failed"}
          </CardTitle>
          <CardDescription>
            {message}
          </CardDescription>
          {email && (
            <p className="text-sm text-muted-foreground">
              Email: <span className="font-medium text-foreground">{email}</span>
            </p>
          )}
        </CardHeader>
        <CardContent className="space-y-4">
          {status === "success" && (
            <div className="space-y-2 p-4 bg-muted/50 rounded-lg">
              <h3 className="font-semibold text-sm">We're sorry to see you go</h3>
              <p className="text-sm text-muted-foreground">
                You will no longer receive our newsletter. If you change your mind, 
                you can always subscribe again from our website.
              </p>
            </div>
          )}
          
          <div className="flex gap-2">
            <Button asChild className="flex-1">
              <Link to="/">
                Back to Home
              </Link>
            </Button>
            <Button asChild variant="outline" className="flex-1">
              <Link to="/shop">
                Browse Products
              </Link>
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
